import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SubHeader from '../../layout/components/SubHeader';
import EcxInlineCalendar from '../../common/EcxInlineCalendar';
import { formatDisplayDate, parseIsoDate, toIsoDate } from '../../common/ecxDateUtils';
import NoAppointment from './NoAppointment';
import AppointmentList from './AppointmentList';
import AppointmentsSkeleton from './subparts/AppointmentsSkeleton';
import appointmentService from '../../../services/appointment.service';
import clinicService from '../../../services/clinic.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import { showError } from '../../../utils/ecxSwal';
import {
    APPOINTMENT_STATUS_OPTIONS,
    formatAppointmentCount,
    getAppointmentTitle,
    isAppointmentConfirmed,
    sortAppointmentsByTime,
} from './subparts/appointmentsQuery';
import { printAppointmentsList } from './subparts/appointmentsPrint';
import './Appointments.css';

const ECP_FILTER_ROLES = ['MANAGER', 'STAFF', 'ECP'];

const getEcpDisplayName = (ecp) => {
    const name = [ecp?.firstName, ecp?.lastName].filter(Boolean).join(' ').trim();

    return name || ecp?.username || 'ECP';
};

const shiftAppointmentDate = (isoDate, days) => {
    const base = parseIsoDate(isoDate) || new Date();
    const next = new Date(base.getFullYear(), base.getMonth(), base.getDate() + days);

    return toIsoDate(next);
};

function Appointments() {
    const user = useEyeconxStore((state) => state.user);

    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [appointmentDate, setAppointmentDate] = useState(() => toIsoDate(new Date()));
    const [appointmentList, setAppointmentList] = useState([]);
    const [selectedEcpId, setSelectedEcpId] = useState('');
    const [ecpList, setEcpList] = useState([]);
    const [ecpListLoading, setEcpListLoading] = useState(false);
    const [checkedItems, setCheckedItems] = useState([]);
    const [statusFilter, setStatusFilter] = useState('ALL');

    const selectedDate = parseIsoDate(appointmentDate) || new Date();
    const apptTitle = useMemo(() => getAppointmentTitle(selectedDate), [appointmentDate]);

    const filteredAppointments = useMemo(
        () =>
            appointmentList.filter((appt) =>
                statusFilter === 'ALL' ? true : String(appt.isConfirmed) === String(statusFilter)
            ),
        [appointmentList, statusFilter]
    );

    const totalAppointment = appointmentList.length;
    const confirmedAppointment = appointmentList.filter(isAppointmentConfirmed).length;
    const filteredCount = filteredAppointments.length;

    const allSelected =
        filteredAppointments.length > 0 && checkedItems.length === filteredAppointments.length;

    const selectedAppointments = useMemo(
        () => filteredAppointments.filter((appt) => checkedItems.includes(appt.id)),
        [filteredAppointments, checkedItems]
    );

    const description = useMemo(() => {
        if (!totalAppointment) {
            return 'View and manage clinic appointments by date';
        }

        return `${formatAppointmentCount(totalAppointment)} appointment${totalAppointment === 1 ? '' : 's'} · ${formatAppointmentCount(confirmedAppointment)} confirmed`;
    }, [confirmedAppointment, totalAppointment]);

    const canFilterByEcp = ECP_FILTER_ROLES.includes(user.role);

    useEffect(() => {
        if (!canFilterByEcp || !user.clinicId) {
            setEcpList([]);
            return;
        }

        setEcpListLoading(true);

        clinicService
            .getUserByRole(user.clinicId, 'ecp')
            .then((res) => setEcpList(Array.isArray(res.data) ? res.data : []))
            .catch(() => {
                setEcpList([]);
                showError({ title: 'Load Failed', text: 'Failed to load ECP list.' });
            })
            .finally(() => setEcpListLoading(false));
    }, [canFilterByEcp, user.clinicId]);

    const loadAppointments = useCallback(
        async ({ silent = false } = {}) => {
            if (!user.clinicId || !appointmentDate) {
                return;
            }

            if (!silent) {
                setLoading(true);
            }

            try {
                const res = await appointmentService.getAppointment(
                    user.clinicId,
                    appointmentDate,
                    selectedEcpId
                );
                const list = Array.isArray(res.data) ? res.data : res.data?.appointmentsList || [];

                setAppointmentList(sortAppointmentsByTime(list));
                setCheckedItems([]);
            } catch (error) {
                console.error(error);
                showError({ title: 'Load Failed', text: 'Failed to load appointments.' });
                setAppointmentList([]);
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        [appointmentDate, selectedEcpId, user.clinicId]
    );

    useEffect(() => {
        loadAppointments();
    }, [loadAppointments]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await loadAppointments({ silent: true });
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleItemCheckChange = (appointmentId, isChecked) => {
        setCheckedItems((prev) => {
            if (isChecked) {
                return prev.includes(appointmentId) ? prev : [...prev, appointmentId];
            }

            return prev.filter((id) => id !== appointmentId);
        });
    };

    const handleToggleAll = (event) => {
        if (event.target.checked) {
            setCheckedItems(filteredAppointments.map((appt) => appt.id));
            return;
        }

        setCheckedItems([]);
    };

    const handlePrint = () => {
        const selectedEcp = ecpList.find((ecp) => ecp.id === selectedEcpId);
        const subtitleParts = [formatDisplayDate(appointmentDate)];

        if (selectedEcp) {
            subtitleParts.push(getEcpDisplayName(selectedEcp));
        }

        const printed = printAppointmentsList({
            appointments: filteredAppointments,
            title: apptTitle,
            subtitle: subtitleParts.join(' · '),
        });

        if (!printed) {
            showError({
                title: 'Print Failed',
                text: 'Unable to open the appointments print preview. Please try again.',
            });
        }
    };

    return (
        <div className="ecx-page appointments-page ecx-page--full-width">
            <SubHeader
                title="Appointments"
                description={description}
                icon="bi-calendar-check"
                containerClass="w-100 px-4"
            >
                <button
                    type="button"
                    className="btn btn-light-primary btn-sm rounded-pill"
                    onClick={handleRefresh}
                    disabled={loading || isRefreshing}
                >
                    <i className={`bi bi-arrow-clockwise me-1 ${isRefreshing ? 'ecx-refresh-spin' : ''}`} />
                    Refresh
                </button>
            </SubHeader>

            <div className="ecx-page-body">
                <div className="appointments-layout">
                    <aside className="appointments-sidebar">
                        <div className="ecx-page-card appointments-sidebar__panel">
                            <div className="appointments-sidebar__header">
                                <span className="ecx-page-icon-badge">
                                    <i className="bi bi-calendar3" />
                                </span>
                                <div>
                                    <h2 className="appointments-sidebar__title">Schedule Date</h2>
                                    <p className="appointments-sidebar__subtitle">
                                        {formatDisplayDate(appointmentDate)}
                                    </p>
                                </div>
                            </div>

                            <div className="appointments-sidebar__picker">
                                <EcxInlineCalendar
                                    className="appointments-sidebar__calendar"
                                    value={appointmentDate}
                                    onChange={setAppointmentDate}
                                    disabled={loading}
                                />
                            </div>

                            <div className="appointments-sidebar__nav">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setAppointmentDate((prev) => shiftAppointmentDate(prev, -1))}
                                    disabled={loading}
                                >
                                    <i className="bi bi-chevron-left me-1" />
                                    Previous
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light-primary"
                                    onClick={() => setAppointmentDate(toIsoDate(new Date()))}
                                    disabled={loading}
                                >
                                    Today
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setAppointmentDate((prev) => shiftAppointmentDate(prev, 1))}
                                    disabled={loading}
                                >
                                    Next
                                    <i className="bi bi-chevron-right ms-1" />
                                </button>
                            </div>

                            <div className="appointments-sidebar__summary">
                                <div className="appointments-sidebar__stat">
                                    <span className="appointments-sidebar__stat-label">Total</span>
                                    <strong className="appointments-sidebar__stat-value">
                                        {formatAppointmentCount(totalAppointment)}
                                    </strong>
                                </div>
                                <div className="appointments-sidebar__stat">
                                    <span className="appointments-sidebar__stat-label">Confirmed</span>
                                    <strong className="appointments-sidebar__stat-value">
                                        {formatAppointmentCount(confirmedAppointment)}
                                    </strong>
                                </div>
                                <div className="appointments-sidebar__stat">
                                    <span className="appointments-sidebar__stat-label">Showing</span>
                                    <strong className="appointments-sidebar__stat-value">
                                        {formatAppointmentCount(filteredCount)}
                                    </strong>
                                </div>
                            </div>

                            {selectedAppointments.length > 0 && (
                                <div className="appointments-sidebar__selection">
                                    <div className="appointments-sidebar__selection-title">
                                        Selected Patients ({selectedAppointments.length})
                                    </div>
                                    <div className="appointments-sidebar__selection-list">
                                        {selectedAppointments.map((appt) => (
                                            <div key={appt.id} className="appointments-sidebar__selection-item">
                                                {appt.patient?.firstName} {appt.patient?.lastName}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </aside>

                    <section className="appointments-main">
                        <div className="ecx-page-card ecx-page-panel appointments-panel">
                            <div className="appointments-filters">
                                <div className="appointments-filters__row">
                                    <div className="appointments-filters__group appointments-filters__group--status">
                                        <span className="appointments-filters__label">Status</span>
                                        <div className="appointments-status-pills">
                                            {APPOINTMENT_STATUS_OPTIONS.map((option) => (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    className={`appointments-status-pill ${
                                                        statusFilter === option.value ? 'is-active' : ''
                                                    }`}
                                                    onClick={() => setStatusFilter(option.value)}
                                                    disabled={loading}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="appointments-filters__actions">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={handlePrint}
                                            disabled={loading}
                                        >
                                            <i className="bi bi-printer me-1" />
                                            Print
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="ecx-page-panel__header border-top-0">
                                <div className="appointments-toolbar">
                                    <div>
                                        <h2 className="appointments-toolbar__title">{apptTitle}</h2>
                                        <div className="appointments-toolbar__meta">
                                            <span className="ecx-page-chip">
                                                Showing {formatAppointmentCount(filteredCount)}
                                            </span>
                                            {checkedItems.length > 0 && (
                                                <span className="ecx-page-chip">
                                                    Selected {formatAppointmentCount(checkedItems.length)}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {canFilterByEcp && (
                                        <div className="appointments-toolbar__actions">
                                            <label
                                                className={`appointments-ecp-filter${
                                                    selectedEcpId ? ' has-value' : ''
                                                }${loading || ecpListLoading ? ' is-disabled' : ''}`}
                                            >
                                                <span className="appointments-ecp-filter__icon" aria-hidden="true">
                                                    <i className="bi bi-person-workspace" />
                                                </span>
                                                <span className="appointments-ecp-filter__field">
                                                    <span className="appointments-ecp-filter__control">
                                                        <span className="appointments-ecp-filter__label">ECP</span>
                                                        <select
                                                            className="appointments-ecp-filter__select"
                                                            value={selectedEcpId}
                                                            onChange={(event) => setSelectedEcpId(event.target.value)}
                                                            disabled={loading || ecpListLoading}
                                                            aria-label="Filter appointments by ECP"
                                                        >
                                                            <option value="">All ECPs</option>
                                                            {ecpList.map((ecp) => (
                                                                <option key={ecp.id} value={ecp.id}>
                                                                    {getEcpDisplayName(ecp)}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </span>
                                                    <span className="appointments-ecp-filter__caret" aria-hidden="true">
                                                        {ecpListLoading ? (
                                                            <span
                                                                className="spinner-border spinner-border-sm appointments-ecp-filter__spinner"
                                                                role="status"
                                                            />
                                                        ) : (
                                                            <i className="bi bi-chevron-down" />
                                                        )}
                                                    </span>
                                                </span>
                                            </label>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="ecx-page-panel__body ecx-page-scroll">
                                {loading ? (
                                    <AppointmentsSkeleton />
                                ) : filteredAppointments.length > 0 ? (
                                    <AppointmentList
                                        appointments={filteredAppointments}
                                        checkedItems={checkedItems}
                                        onItemCheckChange={handleItemCheckChange}
                                        allSelected={allSelected}
                                        onToggleAll={handleToggleAll}
                                        onStatusUpdated={() => loadAppointments({ silent: true })}
                                    />
                                ) : (
                                    <NoAppointment />
                                )}
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}

export default Appointments;
